<script setup>
import { Rive } from '@rive-app/webgl2'
import { useMutationObserver } from '@vueuse/core'
import { onBeforeUnmount, onMounted, ref, useTemplateRef } from 'vue'

import { MODAL_SESSION_ATTRIBUTE } from '@/utils/modal-windows'

import ObjectivesEmptyHero from '@/components/objectives/ObjectivesEmptyHero'

defineOptions({
  name: 'AppRiveScene'
})

const props = defineProps({
  fileSrc: {
    type: String,
    required: true
  },

  autoplay: {
    type: Boolean
  },

  loop: {
    type: Boolean
  },

  height: {
    type: [String, Number],
    default: 320
  },

  width: {
    type: [String, Number],
    default: 320
  },

  syncWithModalSession: {
    type: Boolean
  }
})

const isInitFailed = ref(false)

const canvasReferences = useTemplateRef('canvas')

let riveInstance = null

const observableTarget = document.body

const { stop: stopSyncWithModalSessionObserver } = useMutationObserver(
  observableTarget,
  mutations => {
    mutations.forEach(mutation => {
      const { target, attributeName } = mutation

      if (
        target &&
        attributeName &&
        attributeName === MODAL_SESSION_ATTRIBUTE &&
        target.hasAttribute(attributeName)
      ) {
        pause()
      } else {
        play()
      }
    })
  },
  {
    attributes: true,
    attributeFilter: [MODAL_SESSION_ATTRIBUTE]
  }
)

const runSyncWithModalSessionTask = () => {
  if (props.syncWithModalSession) {
    if (observableTarget.hasAttribute(MODAL_SESSION_ATTRIBUTE)) {
      pause()
    }
  } else {
    stopSyncWithModalSessionObserver()
  }
}

onBeforeUnmount(() => {
  if (props.syncWithModalSession) {
    stopSyncWithModalSessionObserver()
  }
})

const initRive = () => {
  if (canvasReferences.value) {
    riveInstance = new Rive({
      canvas: canvasReferences.value,
      src: props.fileSrc,
      autoplay: props.autoplay,
      loop: props.loop,
      useOffscreenRenderer: true,
      onLoadError: () => {
        isInitFailed.value = true

        stopSyncWithModalSessionObserver()
      },
      onloaderror: () => {
        isInitFailed.value = true

        stopSyncWithModalSessionObserver()
      },
      onLoad: () => {
        riveInstance.resizeDrawingSurfaceToCanvas()

        runSyncWithModalSessionTask()
      }
    })
  } else {
    isInitFailed.value = true
  }
}

onMounted(() => {
  initRive()
})

const play = () => {
  if (isInitFailed.value || !riveInstance) return
  riveInstance.play()
}

const reset = () => {
  if (isInitFailed.value || !riveInstance) return
  riveInstance.reset()
}

const pause = () => {
  if (isInitFailed.value || !riveInstance) return
  riveInstance.pause()
}

defineExpose({
  play,
  reset
})
</script>

<template>
  <div class="ars-Scene">
    <slot v-if="isInitFailed" name="fallback">
      <ObjectivesEmptyHero />
    </slot>
    <canvas v-else ref="canvas" :height="height" :width="width" class="ars-Canvas" />
  </div>
</template>

<style lang="scss" scoped>
.ars-Scene {
  display: flex;
  justify-content: center;
  align-items: center;
}

.ars-Canvas {
  max-height: calc(v-bind(height) * 1px);
  max-width: calc(v-bind(width) * 1px);
}
</style>
